/*
 * A barra de formatação do composer:
 *
 *   [M↓|A]  N  I  Tт  🔗  "  <>  ⬆  ☰  1.  ⇆  ☺  ⊕   ›
 *
 * - O alternador Markdown/"A": Markdown abre o
 *   composer na largura toda com a prévia ao lado; "A" o deixa estreito, sem
 *   a prévia (veja `richMode`).
 * - Negrito e título como letras ("N", "Tт"), aspas de fechamento.
 * - O ⬆ é o upload do fof/upload (que o põe no rodapé), trazido para depois
 *   do código; arrastar e colar imagens continua sendo dele.
 * - ⇆ alterna a direção do texto.
 * - O ⊕ junta o que não aparece direto na barra: tachado,
 *   spoiler, imagem por endereço, menção e a enquete do fof/polls.
 * - "›" aparece quando a barra não cabe e a rola.
 *
 * Tudo entra no primeiro editor, depois das extensões: o flarum/markdown monta
 * a barra por `override`, e um `extend` registrado antes seria descartado.
 * Vale para qualquer TextEditor com composer (o de mensagens também).
 */
import { extend } from 'flarum/common/extend';
import Component from 'flarum/common/Component';
import TextEditor from 'flarum/common/components/TextEditor';
import Button from 'flarum/common/components/Button';
import Icon from 'flarum/common/components/Icon';
import extractText from 'flarum/common/utils/extractText';
import { FIXED_ARROW_GAP, attachFixedArrow } from '../shell/dropdownArrow';
import { editorComposer } from '../features/composer/common';
import { t } from '../utils/settings';
import { morphComposer } from './morph';
const MODE_KEY = 'canela.composerEditor';
let memoryMode = 'rich';

/**
 * Os dois modos do alternador "M↓ | A", lembrados no navegador:
 * - "A" (padrão): o composer estreito, da largura do tópico, sem a prévia ao
 *   lado e só com tela cheia/minimizar/fechar no canto. O Flarum não tem
 *   editor rico; o texto continua sendo escrito no mesmo campo.
 * - Markdown: a largura toda, a prévia ao lado (o « no canto a esconde).
 */
export function richMode() {
  try {
    const value = localStorage.getItem(MODE_KEY);
    return value === null ? true : value === 'rich';
  } catch {
    return memoryMode === 'rich';
  }
}

/** O placeholder do campo conforme o modo, para trocar sem reabrir. */
let placeholderFor = () => '';
export function setPlaceholderProvider(provider) {
  placeholderFor = provider;
}
export function setRichMode(value) {
  memoryMode = value ? 'rich' : 'markdown';
  try {
    localStorage.setItem(MODE_KEY, memoryMode);
  } catch {
    // Vale até sair da página.
  }
  const textarea = document.querySelector('.Composer .TextEditor-editor');
  const placeholder = placeholderFor(value);
  if (textarea && placeholder) textarea.placeholder = placeholder;
  m.redraw();
}
/**
 * Ações extras vindas de fora do editor (a enquete do fof/polls mora no
 * cabeçalho do composer; os modelos do editor, uma ação por modelo). Quem
 * registra devolve uma ação, uma lista delas, ou `null` quando não se aplica.
 */
const externalActions = [];
export function addMoreAction(provider) {
  externalActions.push(provider);
}

/** As ações externas de agora, já achatadas e sem as que não se aplicam. */
function currentExternalActions() {
  return externalActions.flatMap(provider => {
    const result = provider();
    return result ? Array.isArray(result) ? result : [result] : [];
  });
}

/** O alternador Markdown / "A". */
class RichToggle extends Component {
  view() {
    const rich = richMode();
    const label = rich ? t('composer.shell.switch_to_markdown') : t('composer.shell.switch_to_rich');
    return m("button", {
      type: "button",
      className: `CanelaRichToggle ${rich ? '--rte' : '--markdown'}`,
      role: "switch",
      "aria-checked": rich ? 'true' : 'false',
      "aria-label": label,
      title: label,
      onclick: () => morphComposer(() => setRichMode(!rich))
    }, m("span", {
      className: "CanelaRichToggle-slider"
    }, m("span", {
      className: "CanelaRichToggle-left",
      "aria-hidden": "true"
    }, m(Icon, {
      name: "fab fa-markdown"
    })), m("span", {
      className: "CanelaRichToggle-right",
      "aria-hidden": "true"
    }, "A")));
  }
}

/** O menu ⊕: abre para cima, preso à tela, fora da barra (que rola e cortaria). */
class MoreMenu extends Component {
  portal = null;
  outside = null;
  arrow = null;
  view() {
    const label = t('composer.shell.more_options');
    return m(Button, {
      className: "Button Button--icon Button--link CanelaMoreOptions",
      icon: "fas fa-circle-plus",
      title: label,
      "aria-label": label,
      "aria-haspopup": "menu",
      onclick: e => this.toggle(e)
    });
  }
  toggle(e) {
    if (this.portal) return this.close();
    const toggle = e.currentTarget;
    const rect = toggle.getBoundingClientRect();
    const portal = document.createElement('div');
    portal.className = 'CanelaMoreOptions-portal';
    portal.style.left = `${Math.max(8, rect.left)}px`;
    portal.style.bottom = `${window.innerHeight - rect.top + FIXED_ARROW_GAP}px`;
    document.body.appendChild(portal);
    this.portal = portal;
    const render = () => m.render(portal, m("ul", {
      className: "Dropdown-menu CanelaMoreOptions-menu",
      role: "menu"
    }, this.attrs.actions().map(action => m("li", null, m("button", {
      type: "button",
      className: `CanelaMoreOptions-item${action.active ? ' active' : ''}`,
      role: "menuitem",
      onclick: event => {
        this.close();
        action.onclick(event);
        m.redraw();
      }
    }, m(Icon, {
      name: action.icon
    }), m("span", null, action.label))))));
    render();

    // A setinha apontando para o ⊕, como nos menus dos botões de ícone.
    this.arrow = attachFixedArrow(portal, toggle, portal.querySelector('.Dropdown-menu') ?? portal);
    this.outside = event => {
      if (!portal.contains(event.target) && !this.element.contains(event.target)) this.close();
    };
    setTimeout(() => this.outside && document.addEventListener('mousedown', this.outside));
  }
  close() {
    if (this.outside) document.removeEventListener('mousedown', this.outside);
    this.outside = null;
    this.arrow?.remove();
    this.arrow = null;
    if (this.portal) {
      m.render(this.portal, null);
      this.portal.remove();
      this.portal = null;
    }
  }
  onremove(vnode) {
    super.onremove(vnode);
    this.close();
  }
}

/**
 * "‹" e "›": só aparecem quando a barra não cabe e há o que rolar para aquele
 * lado; rolam a barra 160px.
 */
class ScrollButton extends Component {
  view() {
    const left = this.attrs.dir === 'left';
    return m("button", {
      type: "button",
      className: `CanelaToolbarScroll CanelaToolbarScroll--${this.attrs.dir}`,
      "aria-hidden": "true",
      tabindex: -1,
      onclick: () => this.bar()?.scrollBy({
        left: left ? -160 : 160,
        behavior: 'smooth'
      })
    }, m(Icon, {
      name: left ? 'fas fa-chevron-left' : 'fas fa-chevron-right'
    }));
  }
  bar() {
    return this.element?.closest('.TextEditor-toolbar');
  }
  oncreate(vnode) {
    super.oncreate(vnode);
    const bar = this.bar();
    const update = () => {
      if (!bar) return;
      const overflow = bar.scrollWidth - bar.clientWidth > 2;
      const atEnd = bar.scrollLeft + bar.clientWidth >= bar.scrollWidth - 2;
      bar.classList.toggle('has-overflow', overflow && !atEnd);
      bar.classList.toggle('has-overflow-left', overflow && bar.scrollLeft > 2);
    };
    bar?.addEventListener('scroll', update, {
      passive: true
    });
    window.addEventListener('resize', update);
    this.canelaUpdate = update;
    update();
  }
  onupdate(vnode) {
    super.onupdate(vnode);
    this.canelaUpdate?.();
  }
  onremove(vnode) {
    super.onremove(vnode);
    window.removeEventListener('resize', this.canelaUpdate);
  }
}
function fromVnode(vnode, fallbackLabel = '') {
  const attrs = vnode?.attrs;
  if (!attrs || typeof attrs.onclick !== 'function') return null;
  const label = attrs.title || attrs.tooltipText || attrs['aria-label'] || extractText(vnode.children) || fallbackLabel;
  return {
    icon: attrs.icon || 'fas fa-circle',
    label: String(label).replace(/\s*<[^>]+>\s*$/, ''),
    onclick: attrs.onclick
  };
}
export default function installToolbar() {
  // A dica dos botões da barra vai para o <body>: a barra rola na horizontal
  // (overflow), e a dica presa dentro dela saía cortada, por baixo da barra.
  const tooltipToBody = vnode => {
    // Só o vnode do Tooltip (o que tem `text`): sem dica, o botão volta sozinho.
    if (vnode?.attrs && 'text' in vnode.attrs && vnode.attrs.container === undefined) vnode.attrs.container = 'body';
  };
  extend('flarum/common/components/TextEditorButton', 'view', tooltipToBody);
  // Os botões do Markdown (negrito, itálico, listas…) são de outra classe.
  extend('ext:flarum/markdown/common/components/MarkdownButton', 'view', tooltipToBody);
  const ORDER = {
    bold: 1000,
    italic: 990,
    header: 980,
    link: 900,
    quote: 890,
    code: 880,
    unordered_list: 800,
    ordered_list: 790
  };
  const MOVED_TO_MORE = ['strikethrough', 'spoiler', 'image'];
  const UPLOAD_KEYS = ['fof-upload', 'fof-upload-media'];
  let applied = false;
  extend(TextEditor.prototype, 'oninit', function () {
    if (applied) return;
    applied = true;

    // A letra do negrito vem da tradução ("N" em português, "B" em inglês).
    document.documentElement.style.setProperty('--cn-bold-label', JSON.stringify(t('composer.shell.bold_label')));

    // 1) Rodapé: o upload sai dos controles para entrar na barra.
    extend(TextEditor.prototype, 'controlItems', function (items) {
      if (!editorComposer(this)) return;
      this.canelaMore = [];
      this.canelaUploads = UPLOAD_KEYS.filter(key => items.has(key)).map(key => ({
        key,
        vnode: items.get(key)
      }));
      this.canelaUploadsPlaced = false;
      this.canelaUploads.forEach(({
        key
      }) => items.remove(key));
    });

    // 2) Botões do markdown: ordem do tema, letras, extras para o ⊕.
    extend(TextEditor.prototype, 'markdownToolbarItems', function (items) {
      if (!editorComposer(this)) return;
      Object.entries(ORDER).forEach(([key, priority]) => {
        if (items.has(key)) items.setPriority(key, priority);
      });
      const set = (key, icon) => {
        const vnode = items.has(key) ? items.get(key) : null;
        if (vnode?.attrs) vnode.attrs.icon = icon;
      };
      set('bold', 'CanelaTextIcon CanelaTextIcon--bold');
      set('header', 'CanelaTextIcon CanelaTextIcon--heading');
      set('quote', 'fas fa-quote-right');
      MOVED_TO_MORE.forEach(key => {
        if (!items.has(key)) return;
        const action = fromVnode(items.get(key));
        if (action) (this.canelaMore ||= []).push({
          key,
          ...action
        });
        items.remove(key);
      });
      (this.canelaUploads || []).forEach(({
        key,
        vnode
      }) => items.add(key, vnode, 870));
      this.canelaUploadsPlaced = true;
      items.add('canelaDirection', m(Button, {
        className: "Button Button--icon Button--link CanelaDirection",
        icon: "fas fa-right-left",
        title: t('composer.shell.toggle_direction'),
        "aria-label": t('composer.shell.toggle_direction'),
        onclick: () => {
          const textarea = this.element?.querySelector('textarea');
          if (textarea) textarea.dir = textarea.dir === 'rtl' ? 'ltr' : 'rtl';
        }
      }), 780);
    });

    // 3) A barra inteira: alternador, menção para o ⊕, o ⊕ e o "›".
    extend(TextEditor.prototype, 'toolbarItems', function (items) {
      if (!editorComposer(this)) return;

      // Sem o flarum/markdown, o upload fica na barra mesmo assim.
      if (!this.canelaUploadsPlaced) (this.canelaUploads || []).forEach(({
        key,
        vnode
      }) => items.add(key, vnode, 90));
      items.add('canelaRich', m(RichToggle, null), 10000);
      if (items.has('mention')) {
        const action = fromVnode(items.get('mention'));
        if (action) (this.canelaMore ||= []).push({
          key: 'mention',
          ...action
        });
        items.remove('mention');
      }
      const editor = this;
      const actions = () => [...currentExternalActions(), ...(editor.canelaMore || [])];
      if (actions().length) items.add('canelaMore', m(MoreMenu, {
        actions: actions
      }), -1000);
      items.add('canelaScrollBack', m(ScrollButton, {
        dir: "left"
      }), 2000);
      items.add('canelaScroll', m(ScrollButton, {
        dir: "right"
      }), -2000);
    });
  });
}
flarum.reg.add('ramon-canela', 'forum/composer/installToolbar', installToolbar);